import { useEffect, useState } from 'react';
import { useAuth } from 'zite-auth-sdk';
import { getFeedbackCentre, saveFeedback, convertFeedbackToAction, GetFeedbackCentreOutputType } from 'zite-endpoints-sdk';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { MessageSquarePlus, Search, Plus, Lightbulb, HelpCircle, MessageCircle } from 'lucide-react';
import { formatDistanceToNow, parseISO } from 'date-fns';

export default function FeedbackCentre() {
  const { user } = useAuth();
  const [data, setData] = useState<GetFeedbackCentreOutputType | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [submitDialog, setSubmitDialog] = useState(false);
  const [respondDialog, setRespondDialog] = useState<any>(null);
  const [form, setForm] = useState({ subject: '', type: 'Question', content: '', category: 'General', anonymous: false });
  const [response, setResponse] = useState({ adminResponse: '', status: 'Answered', visibleToTeam: false });
  const [convertDialog, setConvertDialog] = useState<any>(null);
  const [actionForm, setActionForm] = useState({ taskName: '', description: '', dueDate: '' });

  useEffect(() => { if (user) load(); }, [user]);
  const load = () => { setLoading(true); getFeedbackCentre({ search: search || undefined, type: typeFilter || undefined }).then(setData).finally(() => setLoading(false)); };
  useEffect(() => { if (user) load(); }, [search, typeFilter]);

  const handleSubmit = async () => {
    if (!form.subject) return;
    try {
      await saveFeedback({ action: 'submit', ...form });
      toast.success('Submitted');
      setSubmitDialog(false);
      setForm({ subject: '', type: 'Question', content: '', category: 'General', anonymous: false });
      load();
    } catch { toast.error('Failed'); }
  };

  const handleRespond = async () => {
    if (!respondDialog) return;
    try {
      await saveFeedback({ action: 'respond', id: respondDialog.id, ...response });
      toast.success('Response saved');
      setRespondDialog(null);
      load();
    } catch { toast.error('Failed'); }
  };

  const handleStatus = async (id: string, status: string) => {
    try {
      await saveFeedback({ action: 'updateStatus', id, status });
      toast.success('Updated');
      load();
    } catch { toast.error('Failed'); }
  };

  const typeIcons: Record<string, any> = { Question: HelpCircle, Idea: Lightbulb, Feedback: MessageCircle, Suggestion: Lightbulb };
  const statusColors: Record<string, string> = { New: 'bg-blue-500/10 text-blue-700', Reviewing: 'bg-yellow-500/10 text-yellow-700', Answered: 'bg-green-500/10 text-green-700', Actioned: 'bg-emerald-500/10 text-emerald-700', Parked: 'bg-purple-500/10 text-purple-700', Closed: 'bg-muted text-muted-foreground', Open: 'bg-blue-500/10 text-blue-700', 'In Review': 'bg-yellow-500/10 text-yellow-700', Resolved: 'bg-green-500/10 text-green-700' };

  if (loading) return <div className="p-6 space-y-4">{[1,2,3].map(i => <Skeleton key={i} className="h-20 w-full" />)}</div>;

  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><MessageSquarePlus className="w-6 h-6 text-primary" /> Questions, Ideas & Feedback</h1>
          <p className="text-sm text-muted-foreground mt-1">Ask questions, share ideas, or give feedback</p>
        </div>
        <Button size="sm" onClick={() => setSubmitDialog(true)}><Plus className="w-4 h-4 mr-1" /> Submit</Button>
      </div>

      <div className="flex flex-wrap gap-2">
        <div className="relative flex-1 max-w-xs">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-36"><SelectValue placeholder="All Types" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            {['Question','Idea','Feedback','Suggestion','Report a Problem','Confidential Report'].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Tabs defaultValue="submissions">
        <TabsList>
          <TabsTrigger value="submissions">Submissions ({data?.submissions.length || 0})</TabsTrigger>
          <TabsTrigger value="faqs">Team FAQs ({data?.teamFaqs.length || 0})</TabsTrigger>
        </TabsList>

        <TabsContent value="submissions" className="space-y-3 mt-4">
          {(data?.submissions || []).map((s: any) => {
            const Icon = typeIcons[s.type] || MessageCircle;
            return (
              <Card key={s.id} className={data?.isManager ? 'cursor-pointer hover:shadow-md' : ''} onClick={() => { if (data?.isManager) { setRespondDialog(s); setResponse({ adminResponse: s.adminResponse || '', status: s.status || 'Answered', visibleToTeam: s.visibleToTeam || false }); } }}>
                <CardContent className="py-4">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0"><Icon className="w-4 h-4 text-primary" /></div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold">{s.subject}</h3>
                        <Badge variant="outline" className="text-xs">{s.type}</Badge>
                        <Badge className={statusColors[s.status] || 'bg-muted text-muted-foreground'}>{s.status}</Badge>
                        {s.anonymous && <Badge variant="outline" className="text-xs">Anonymous</Badge>}
                        {s.category && <Badge variant="outline" className="text-xs">{s.category}</Badge>}
                      </div>
                      <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{s.content}</p>
                      {s.adminResponse && <div className="mt-2 p-2 bg-muted/50 rounded text-sm"><strong className="text-xs text-primary">Response:</strong> {s.adminResponse}</div>}
                      {s.submittedAt && <p className="text-xs text-muted-foreground mt-1">{formatDistanceToNow(parseISO(s.submittedAt), { addSuffix: true })}</p>}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
          {(data?.submissions || []).length === 0 && <p className="text-muted-foreground text-sm text-center py-8">No submissions</p>}
        </TabsContent>

        <TabsContent value="faqs" className="space-y-3 mt-4">
          {(data?.teamFaqs || []).map((s: any) => (
            <Card key={s.id}>
              <CardContent className="py-4">
                <h3 className="font-semibold">{s.subject}</h3>
                <p className="text-sm text-muted-foreground mt-1">{s.content}</p>
                {s.adminResponse && <div className="mt-2 p-2 bg-primary/5 rounded text-sm"><strong className="text-xs text-primary">Answer:</strong> {s.adminResponse}</div>}
              </CardContent>
            </Card>
          ))}
        </TabsContent>
      </Tabs>

      {/* Submit Dialog */}
      <Dialog open={submitDialog} onOpenChange={setSubmitDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Submit Question, Idea or Feedback</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Subject" value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} />
            <Select value={form.type} onValueChange={v => setForm({ ...form, type: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{['Question','Idea','Feedback','Suggestion','Report a Problem','Confidential Report'].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{['General','Training','Operations','Facilities','Menu','Wellbeing','Other'].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
            <Textarea placeholder="Your question, idea or feedback" value={form.content} onChange={e => setForm({ ...form, content: e.target.value })} rows={4} />
            <div className="flex items-center gap-2"><Switch checked={form.anonymous} onCheckedChange={v => setForm({ ...form, anonymous: v })} /><span className="text-sm">Submit anonymously</span></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setSubmitDialog(false)}>Cancel</Button><Button onClick={handleSubmit}>Submit</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Respond Dialog */}
      <Dialog open={!!respondDialog} onOpenChange={() => setRespondDialog(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Respond to: {respondDialog?.subject}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm">{respondDialog?.content}</p>
            <Textarea placeholder="Your response" value={response.adminResponse} onChange={e => setResponse({ ...response, adminResponse: e.target.value })} rows={3} />
            <Select value={response.status} onValueChange={v => setResponse({ ...response, status: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{['New','Reviewing','Answered','Actioned','Parked','Closed'].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
            <div className="flex items-center gap-2"><Switch checked={response.visibleToTeam} onCheckedChange={v => setResponse({ ...response, visibleToTeam: v })} /><span className="text-sm">Make visible to team (as FAQ)</span></div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setConvertDialog(respondDialog); setActionForm({ taskName: respondDialog?.subject || '', description: respondDialog?.content || '', dueDate: '' }); }}>Convert to Action</Button>
            <Button variant="outline" onClick={() => setRespondDialog(null)}>Cancel</Button>
            <Button onClick={handleRespond}>Save Response</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Convert to Action Dialog */}
      <Dialog open={!!convertDialog} onOpenChange={() => setConvertDialog(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Convert to Action Task</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">Creates a linked manager task. Original feedback {convertDialog?.anonymous ? '(anonymous — identity protected)' : ''} remains linked.</p>
          <div className="space-y-3 mt-2">
            <Input placeholder="Task name" value={actionForm.taskName} onChange={e => setActionForm(f => ({ ...f, taskName: e.target.value }))} />
            <Textarea placeholder="Description" value={actionForm.description} onChange={e => setActionForm(f => ({ ...f, description: e.target.value }))} rows={3} />
            <Input type="date" value={actionForm.dueDate} onChange={e => setActionForm(f => ({ ...f, dueDate: e.target.value }))} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConvertDialog(null)}>Cancel</Button>
            <Button onClick={async () => {
              if (!actionForm.taskName) { toast.error('Task name required'); return; }
              try {
                await convertFeedbackToAction({ feedbackId: convertDialog.id, taskName: actionForm.taskName, description: actionForm.description, dueDate: actionForm.dueDate || undefined });
                toast.success('Action task created');
                setConvertDialog(null);
                setRespondDialog(null);
                load();
              } catch (e: any) { toast.error(e.message || 'Failed'); }
            }}>Create Action</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
